/**
 * Per-project deployments ledger UI.
 *
 * Shows every deploy newest-first with:
 *   • status pill (live / queued / building / superseded / failed)
 *   • preview URL (clickable when live)
 *   • snapshot summary + short SHA
 *   • who deployed + when
 *   • rollback indicator (this row was a rollback OF another)
 *   • "Rollback to here" button on superseded/failed past deploys (W2-P3b
 *     wires the click handler)
 *
 * Read-only in W2-P3a; rollback button gets functional in W2-P3b.
 */
import { useEffect, useState } from 'react'
import { ExternalLink, Rocket, RotateCcw } from 'lucide-react'
import { api, type ApiError, type DeploymentDto } from '../lib/api'
import { Badge, StatusBox, type BadgeVariant } from './ui/badge'
import { Button } from './ui/button'
import { EmptyState } from './ui/empty-state'

type Props = {
  projectId: string
  accessToken: string
  refreshKey: number
  // W2-P3b will pass this in; ignored for now.
  onRollback?: (snapshotId: string) => void
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  queued: 'volt',
  building: 'volt',
  live: 'success',
  failed: 'destructive',
  superseded: 'outline',
}

export function Deployments({ projectId, accessToken, refreshKey, onRollback }: Props) {
  const [deployments, setDeployments] = useState<DeploymentDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api
      .listDeployments(accessToken, projectId)
      .then((res) => {
        if (!cancelled) {
          setDeployments(res.deployments)
          setError(null)
        }
      })
      .catch((err: ApiError) => {
        if (!cancelled) setError(err.title ?? 'Failed to load deployments')
      })
    return () => {
      cancelled = true
    }
  }, [accessToken, projectId, refreshKey])

  if (error) {
    return (
      <div className="p-4">
        <StatusBox tone="destructive">{error}</StatusBox>
      </div>
    )
  }
  if (deployments === null) return <p className="p-5 text-sm text-muted-foreground">Loading deployments…</p>
  if (deployments.length === 0) {
    return (
      <div className="p-4">
        <EmptyState
          icon={<Rocket />}
          title="No deployments yet"
          body="Click Deploy in the project header to publish your changes to a live URL."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Deployments</h3>
        <Badge variant="secondary" className="tabular-nums">{deployments.length} total</Badge>
      </header>
      <ol className="min-h-0 flex-1 divide-y overflow-y-auto">
        {deployments.map((d) => {
          const variant = STATUS_VARIANT[d.status] ?? 'outline'
          const isLive = d.status === 'live'
          const canRollback =
            d.status === 'superseded' && d.url !== null && onRollback
          return (
            <li key={d.id} className="px-4 py-3 transition-colors hover:bg-muted/50">
              <div className="flex items-center justify-between gap-3">
                <Badge variant={variant} className="uppercase tracking-wide">{d.status}</Badge>
                <time className="shrink-0 text-xs text-muted-foreground" dateTime={d.startedAt}>
                  {formatRelative(d.startedAt)}
                </time>
              </div>

              <p className="mt-1 line-clamp-2 text-sm text-foreground">
                {d.snapshotSummary ?? <span className="italic text-muted-foreground">(no summary)</span>}
              </p>

              <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs text-muted-foreground">
                <span className="font-mono">{d.snapshotGitSha.slice(0, 7)}</span>
                {d.url && (
                  isLive ? (
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-primary underline-offset-4 hover:underline"
                    >
                      open <ExternalLink className="size-3" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground/70 line-through">{d.url}</span>
                  )
                )}
                {d.durationMs !== null && (
                  <span className="tabular-nums">{(d.durationMs / 1000).toFixed(1)}s</span>
                )}
                {d.triggeredByEmail && <span>by {d.triggeredByEmail}</span>}
                {d.rollbackOf && (
                  <span className="inline-flex items-center gap-1 text-warning-ink">
                    <RotateCcw className="size-3" /> rollback
                  </span>
                )}
                {d.errorReason && (
                  <span className="line-clamp-1 text-destructive">{d.errorReason}</span>
                )}
              </div>

              {canRollback && (
                <Button
                  variant="outline"
                  size="xs"
                  className="mt-2"
                  onClick={() => onRollback(d.snapshotId)}
                >
                  <RotateCcw /> Rollback to here
                </Button>
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'just now'
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`
  if (ms < 604_800_000) return `${Math.floor(ms / 86_400_000)}d ago`
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
